import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown, ArrowUp, Lock, Plus, RefreshCw, Tags, Trash2,
} from 'lucide-react';
import { FieldLabel } from '@documents-ui';
import type { CategoryAppliesTo, DocumentCategoryRecord, LegalDocument } from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { documentsApi } from '../../services/documentsApi';
import { toastService } from '../../services/toastService';
import { refreshCategories } from './documentCategories';
import { Button, INPUT_CLASS, ModalShell, Tag, WarnNote } from './rubyChrome';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / CATEGORIES — "where is this document offered?", managed as data.
 *
 * This axis used to be a hardcoded enum, so adding "Supplier NDA" meant editing
 * shared-types and deploying four repos. It is rows now, and this is where they
 * are edited.
 *
 * TWO KINDS OF ROW, and the difference is the whole page:
 *
 *   • BUILT-IN — seeded by documents-service and NAMED IN CODE. The financing
 *     wizard asks for `device_finance` by id, so deleting it would silently
 *     empty that picker with nothing on screen to explain why. They can be
 *     relabelled, reordered and retired; they cannot be deleted, and the id is
 *     shown so you can see what code is asking for.
 *   • CUSTOM — created here. Deletable outright, but only while no document is
 *     filed under it: the service counts first and refuses with the number.
 *
 * RETIRE IS THE SAFE VERB for both. It removes a category from every picker
 * while leaving the row readable, so documents already filed under it keep
 * their label instead of rendering a raw id.
 */

const APPLIES_TO_LABEL: Record<CategoryAppliesTo, string> = {
  agreement: 'Agreements',
  policy: 'Policies',
  both: 'Both lists',
};

const APPLIES_TO_OPTIONS: { value: CategoryAppliesTo; label: string; subtitle: string }[] = [
  { value: 'agreement', label: 'Agreements only', subtitle: 'Signed — offered on the Agreements list' },
  { value: 'policy', label: 'Policies only', subtitle: 'Accepted — offered on the Policies list' },
  { value: 'both', label: 'Both lists', subtitle: 'Offered wherever a document is filed' },
];

export default function CategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<DocumentCategoryRecord[]>([]);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentCategoryRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Documents come along so the table can say how many are filed under each
      // category — the one number that decides whether deleting is safe, and
      // the reason a "0 documents" category is obviously prunable.
      const [cats, docs] = await Promise.all([
        documentsApi.listCategories(),
        documentsApi.listDocuments().catch(() => ({ documents: [] as LegalDocument[], count: 0 })),
      ]);
      setCategories(cats.categories || []);
      setDocuments(docs.documents || []);
      await refreshCategories();
    } catch (e: any) {
      toastService.error(e?.message || 'category_list_failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const usage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of documents) {
      if (d.category) counts.set(d.category, (counts.get(d.category) ?? 0) + 1);
    }
    return counts;
  }, [documents]);

  const uncategorised = documents.filter((d) => !d.category).length;

  const patch = async (categoryId: string, body: Parameters<typeof documentsApi.updateCategory>[1]) => {
    setBusyId(categoryId);
    try {
      await documentsApi.updateCategory(categoryId, body);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'category_update_failed');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Reorder by SWAPPING sortOrder with the neighbour, rather than renumbering
   * the list. Two writes, no cascade, and an operator who has deliberately
   * spaced their own numbers keeps them.
   */
  const move = async (index: number, direction: -1 | 1) => {
    const target = categories[index + direction];
    const self = categories[index];
    if (!target || !self) return;
    setBusyId(self.categoryId);
    try {
      await documentsApi.updateCategory(self.categoryId, { sortOrder: target.sortOrder });
      await documentsApi.updateCategory(target.categoryId, { sortOrder: self.sortOrder });
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'category_update_failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (cat: DocumentCategoryRecord) => {
    setBusyId(cat.categoryId);
    try {
      await documentsApi.deleteCategory(cat.categoryId);
      toastService.success(`${cat.label} deleted`);
      await load();
    } catch (e: any) {
      // The service refuses with a REASON — a built-in, or a live document
      // count. Both are worth showing verbatim; neither is a failure to retry.
      toastService.error(e?.message || 'category_delete_failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={Tags}
      title="Document categories"
      subtitle="Where each document is offered — the axis every picker in the product filters on."
      actions={(
        <>
          <Button onClick={() => void load()} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            New category
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-stone-200 bg-white p-4 text-[12.5px] leading-relaxed text-stone-600 shadow-sm">
          A document&rsquo;s <b className="text-stone-800">folder</b> is how you file it — renamed
          and reorganised at will, and nothing in the product reads it. Its{' '}
          <b className="text-stone-800">category</b> is where the product <i>offers</i> it: the
          device screens show device categories, checkout shows checkout ones, and a document
          with no category appears in no picker at all. Categories marked{' '}
          <b className="text-stone-800">built-in</b> are named directly in code, so they can be
          relabelled or retired but never deleted.
          {uncategorised > 0 && (
            <>
              {' '}<b className="text-stone-800">{uncategorised} document{uncategorised === 1 ? '' : 's'}</b>{' '}
              currently {uncategorised === 1 ? 'has' : 'have'} no category and{' '}
              {uncategorised === 1 ? 'is' : 'are'} offered nowhere —{' '}
              <button type="button" className="underline hover:text-stone-900" onClick={() => navigate('/legal/policies')}>
                review them
              </button>.
            </>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-stone-50">
                  {['Category', 'Id (used by code)', 'Shows on', 'Documents', 'Status', 'Order', ''].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap border-b border-stone-200 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wider text-stone-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-3">
                      <RubyLoader variant="section" label="Loading categories…" />
                    </td>
                  </tr>
                )}
                {!loading && categories.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-[13px] text-stone-500">
                      No categories. The built-in set is created automatically the first time this
                      page loads — try Refresh.
                    </td>
                  </tr>
                )}
                {!loading && categories.map((cat, i) => {
                  const inUse = usage.get(cat.categoryId) ?? 0;
                  const busy = busyId === cat.categoryId;
                  const retired = cat.status === 'retired';
                  return (
                    <tr key={cat.categoryId} className={retired ? 'bg-stone-50/60' : undefined}>
                      <td className="px-3 py-3 align-middle">
                        <div className={`text-[13px] font-medium ${retired ? 'text-stone-400' : 'text-stone-900'}`}>
                          {cat.label}
                        </div>
                        {cat.hint && <div className="mt-0.5 max-w-[380px] text-[11px] text-stone-400">{cat.hint}</div>}
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px] text-stone-700">
                          {cat.categoryId}
                        </code>
                        {cat.builtIn && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] text-stone-400" title="Named in code — cannot be deleted">
                            <Lock size={11} />built-in
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-stone-600">
                        {APPLIES_TO_LABEL[cat.appliesTo]}
                      </td>
                      <td className="px-3 py-3 align-middle text-[13px] text-stone-600">{inUse}</td>
                      <td className="px-3 py-3 align-middle">
                        <Tag tone={retired ? 'retired' : 'active'}>{retired ? 'Retired' : 'Active'}</Tag>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 align-middle">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            aria-label={`Move ${cat.label} up`}
                            disabled={i === 0 || busy}
                            onClick={() => void move(i, -1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700 disabled:opacity-30"
                          >
                            <ArrowUp size={14} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${cat.label} down`}
                            disabled={i === categories.length - 1 || busy}
                            onClick={() => void move(i, 1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700 disabled:opacity-30"
                          >
                            <ArrowDown size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right align-middle">
                        <div className="flex justify-end gap-2">
                          <Button onClick={() => setEditing(cat)} disabled={busy}>Edit</Button>
                          <Button
                            onClick={() => void patch(cat.categoryId, { status: retired ? 'active' : 'retired' })}
                            disabled={busy}
                          >
                            {retired ? 'Restore' : 'Retire'}
                          </Button>
                          {!cat.builtIn && (
                            <Button
                              variant="danger"
                              onClick={() => void remove(cat)}
                              disabled={busy || inUse > 0}
                              title={inUse > 0 ? `${inUse} document(s) are filed here — move them first, or retire it` : 'Delete permanently'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <WarnNote>
          <b>Retire, don&rsquo;t delete, anything that has ever been used.</b> Retiring removes a
          category from every picker but keeps the row, so the documents filed under it still read
          with a proper label instead of a raw id. Deleting is offered only for a category nobody
          created a document under — and never for a built-in, because code names those ids
          directly and the picker would empty with nothing on screen to say why.
        </WarnNote>
      </div>

      {addOpen && (
        <CategoryModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); void load(); }}
        />
      )}
      {editing && (
        <CategoryModal
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </StaffPage>
  );
}

/* ── new / edit category ────────────────────────────────────────────────── */

/**
 * One form for both, because they differ in exactly one field: the id, which is
 * derived from the label on create and IMMUTABLE afterwards. Documents store
 * the id, so letting it change would orphan every document filed under it.
 */
function CategoryModal({
  category, onClose, onSaved,
}: {
  category?: DocumentCategoryRecord;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!category;
  const [label, setLabel] = useState(category?.label ?? '');
  const [hint, setHint] = useState(category?.hint ?? '');
  const [appliesTo, setAppliesTo] = useState<CategoryAppliesTo>(category?.appliesTo ?? 'both');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirrors slugifyCategoryId in shared-types — shown live so the id somebody
  // will later hardcode is visible BEFORE it becomes permanent.
  const previewId = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);

  const submit = async () => {
    if (!label.trim()) { setError('Give the category a name.'); return; }
    if (!isEdit && !previewId) { setError('That name has no letters or digits to build an id from.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await documentsApi.updateCategory(category!.categoryId, {
          label: label.trim(), hint: hint.trim() || undefined, appliesTo,
        });
        toastService.success('Category updated');
      } else {
        await documentsApi.createCategory({ label: label.trim(), hint: hint.trim() || undefined, appliesTo });
        toastService.success(`${label.trim()} created`);
      }
      onSaved();
    } catch (e: any) {
      setError(
        e?.message === 'category_already_exists'
          ? 'A category with that id already exists. Ids are permanent because documents are filed under them.'
          : e?.message || 'category_save_failed',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Edit category' : 'New category'}
      subtitle={isEdit
        ? 'The id is permanent — documents are filed under it — but everything else can change.'
        : 'Categories decide where a document is offered in the product.'}
      onClose={onClose}
      footerNote={isEdit ? `Id: ${category!.categoryId}` : 'The id is derived from the name and is permanent.'}
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={saving || !label.trim()}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create category'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel requirement="required">Name</FieldLabel>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Supplier NDA"
            className={INPUT_CLASS}
          />
          {!isEdit && (
            <p className="mt-1.5 text-[11.5px] text-stone-500">
              Id: <code className="rounded bg-stone-100 px-1 font-mono text-stone-700">{previewId || '…'}</code>
              {' '}— permanent, and what code refers to if this category is ever wired into a screen.
            </p>
          )}
        </div>

        <div>
          <FieldLabel>Hint</FieldLabel>
          <input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Signed before anything confidential is shared"
            className={INPUT_CLASS}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Shown under the name in every dropdown. Say where it is actually offered — that is the
            whole contract with the person choosing.
          </p>
        </div>

        <div>
          <FieldLabel requirement="required">Shows on</FieldLabel>
          <SearchableDropdown
            options={APPLIES_TO_OPTIONS}
            value={appliesTo}
            onChange={(v) => setAppliesTo((v as CategoryAppliesTo) || 'both')}
            placeholder="Which list offers this?"
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Agreements are signed, policies are accepted. Choosing one keeps the other list&rsquo;s
            dropdown short and honest.
          </p>
        </div>

        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">{error}</p>
        )}
      </div>
    </ModalShell>
  );
}
